<template>
  <div class="page-facturation">
    <landing-header />

    <main>

      <!-- ===== HERO ===== -->
      <section class="hero-facturation">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-facturation">Inclus dans votre abonnement entreprise</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              La facturation des chauffagistes,<br />
              <span class="gradient-text-orange">enfin pensée pour eux</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Devis en 3 minutes, factures conformes à la loi anti-fraude TVA, suivi des paiements en temps réel — tout intégré dans Termoflow, sans abonnement supplémentaire, sans double saisie.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
            </div>

            <!-- Social proof -->
            <div class="hero-stats">
              <div class="stat-pill">
                <span class="stat-val">3 min</span>
                <span class="stat-lab">pour créer un devis</span>
              </div>
              <div class="stat-pill">
                <span class="stat-val">100%</span>
                <span class="stat-lab">conforme loi anti-fraude</span>
              </div>
              <div class="stat-pill">
                <span class="stat-val">0 €</span>
                <span class="stat-lab">d'abonnement en plus</span>
              </div>
            </div>
          </div>

          <!-- UI mockup -->
          <div class="lg:w-1/2 w-full">
            <div class="invoice-ui shadow-2xl">
              <div class="ui-header">
                <div class="ui-tabs">
                  <span class="ui-tab active">Devis #DEV-2025-089</span>
                  <span class="ui-tab">Facture</span>
                  <span class="ui-tab">Acompte</span>
                </div>
                <div class="ui-status validated">✓ Validé</div>
              </div>
              <div class="ui-body">
                <div class="invoice-client-row">
                  <div>
                    <div class="inv-label">Client</div>
                    <div class="inv-val">M. Dupont — 12 rue des Lilas, Lyon</div>
                  </div>
                  <div>
                    <div class="inv-label">Date</div>
                    <div class="inv-val">14 mai 2025</div>
                  </div>
                </div>
                <div class="invoice-lines">
                  <div class="inv-line-head">
                    <span>Désignation</span><span>Qté</span><span>Prix HT</span><span>TVA</span><span>Total TTC</span>
                  </div>
                  <div class="inv-line">
                    <span>Installation PAC air/eau DAIKIN 8kW</span><span>1</span><span>3 800 €</span><span>5,5%</span><span>4 009 €</span>
                  </div>
                  <div class="inv-line">
                    <span>Main d'œuvre installation (8h)</span><span>8h</span><span>65 €</span><span>10%</span><span>572 €</span>
                  </div>
                  <div class="inv-line highlight-line">
                    <span>Fluide R32 (recharge 2 kg)</span><span>2</span><span>48 €</span><span>20%</span><span>115,20 €</span>
                  </div>
                </div>
                <div class="inv-totals">
                  <div class="tot-row"><span>Total HT</span><span>4 396 €</span></div>
                  <div class="tot-row"><span>TVA (mixte)</span><span>300,20 €</span></div>
                  <div class="tot-row bold"><span>Total TTC</span><span>4 696,20 €</span></div>
                  <div class="tot-row acompte"><span>Acompte (30%)</span><span>— 1 408,86 €</span></div>
                  <div class="tot-row solde"><span>Solde restant dû</span><span>3 287,34 €</span></div>
                </div>
                <div class="inv-actions">
                  <button class="inv-btn-primary">Convertir en facture</button>
                  <button class="inv-btn-sec">Envoyer par email</button>
                  <button class="inv-btn-sec">Aperçu PDF</button>
                </div>
              </div>
              <div class="ui-footer-seal">
                <i class="fa-solid fa-lock" style="font-size:0.75rem;"></i>
                Document scellé SHA-256 — conforme loi anti-fraude TVA
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== BARRE DE CONFIANCE ===== -->
      <section class="trust-bar">
        <div class="max-w-7xl mx-auto px-4">
          <div class="trust-items">
            <div class="trust-item">
              <i class="fa-solid fa-shield-halved"></i>
              <span>Certifié loi anti-fraude TVA (RIAL)</span>
            </div>
            <div class="trust-sep"></div>
            <div class="trust-item">
              <i class="fa-solid fa-file-invoice"></i>
              <span>e-invoicing 2026 ready</span>
            </div>
            <div class="trust-sep"></div>
            <div class="trust-item">
              <i class="fa-solid fa-rotate"></i>
              <span>Devis → Facture en 1 clic</span>
            </div>
            <div class="trust-sep"></div>
            <div class="trust-item">
              <i class="fa-solid fa-mobile-screen-button"></i>
              <span>Utilisable sur chantier</span>
            </div>
            <div class="trust-sep"></div>
            <div class="trust-item">
              <i class="fa-solid fa-euro-sign"></i>
              <span>Inclus sans supplément</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION DEVIS ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="section-label">Devis &amp; Factures</div>
          <h2 class="section-title">Créez vos devis comme un pro,<br />en quelques minutes</h2>
          <p class="section-sub">Fini les tableurs bricolés et les PDF retouchés à la main. Termoflow génère des documents professionnels, à votre image, depuis votre téléphone ou votre bureau.</p>

          <div class="features-grid-3">

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-calculator"></i></div>
              <h3>Calcul automatique des marges</h3>
              <p>Saisissez votre prix d'achat et votre marge cible, le prix de vente HT/TTC est calculé instantanément. Vous visualisez votre rentabilité avant même d'envoyer le devis.</p>
            </div>

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-percent"></i></div>
              <h3>Multi-taux TVA natif</h3>
              <p>5,5 %, 10 %, 20 % — les taux s'appliquent ligne par ligne selon la nature des travaux. Idéal pour les chantiers de rénovation énergétique où plusieurs taux coexistent sur le même document.</p>
            </div>

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-layer-group"></i></div>
              <h3>Catalogue de prestations</h3>
              <p>Constituez votre bibliothèque de lignes : main d'œuvre, fournitures, pièces détachées, PAC, VMC, plomberie... Ajoutez-les d'un clic sur n'importe quel document sans ressaisir à chaque fois.</p>
            </div>

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-arrow-right-arrow-left"></i></div>
              <h3>Conversion devis → facture en 1 clic</h3>
              <p>Dès le chantier terminé, convertissez votre devis en facture. Si un acompte a été demandé, Termoflow génère automatiquement la facture d'acompte <em>et</em> la facture de solde, avec les bons montants.</p>
            </div>

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-tags"></i></div>
              <h3>Remises &amp; avoirs</h3>
              <p>Appliquez une remise globale ou ligne par ligne. En cas d'erreur ou de litige, créez un avoir en quelques secondes, sans repartir de zéro.</p>
            </div>

            <div class="feat-card">
              <div class="feat-icon orange"><i class="fa-solid fa-copy"></i></div>
              <h3>Documents dupliquables</h3>
              <p>Réutilisez un devis ou une facture existante comme base de travail. Modifiez ce qui change, conservez ce qui reste. Gagnez du temps sur les chantiers récurrents ou similaires.</p>
            </div>

          </div>
        </div>
      </section>

      <!-- ===== SECTION CONFORMITÉ (DARK) ===== -->
      <section class="section-dark py-24">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-center">
            <div class="lg:w-1/2">
              <div class="section-label-light">Conformité légale</div>
              <h2 class="text-3xl font-bold text-white mb-6">Un logiciel de facturation certifié,<br />qui vous protège vraiment</h2>
              <p class="text-slate-400 mb-10 leading-relaxed">
                La loi anti-fraude TVA impose des obligations strictes aux logiciels de caisse et de facturation. Termoflow est conçu depuis le premier jour pour y répondre intégralement — pas un patch, une architecture.
              </p>

              <div class="compliance-list">

                <div class="compliance-item">
                  <div class="comp-icon"><i class="fa-solid fa-fingerprint"></i></div>
                  <div>
                    <h4>Scellement SHA-256 infalsifiable</h4>
                    <p>Chaque document validé est scellé par un hash cryptographique. Toute modification ultérieure est détectable. Vous êtes inattaquable en cas de contrôle fiscal.</p>
                  </div>
                </div>

                <div class="compliance-item">
                  <div class="comp-icon"><i class="fa-solid fa-list-ol"></i></div>
                  <div>
                    <h4>Numérotation séquentielle sans trou</h4>
                    <p>Générée automatiquement dès la validation. Aucun saut, aucun doublon, aucune facture supprimée après émission — exactement ce qu'exige le RIAL.</p>
                  </div>
                </div>

                <div class="compliance-item">
                  <div class="comp-icon"><i class="fa-solid fa-timeline"></i></div>
                  <div>
                    <h4>Audit trail complet</h4>
                    <p>Chaque action sur vos documents est tracée : qui, quand, depuis quelle IP. Un historique exhaustif, consultable à tout moment, opposable à l'administration.</p>
                  </div>
                </div>

                <div class="compliance-item">
                  <div class="comp-icon"><i class="fa-solid fa-vault"></i></div>
                  <div>
                    <h4>Archivage sécurisé 6 ans</h4>
                    <p>Vos PDF sont hébergés et conservés conformément aux obligations fiscales françaises. Aucun document ne peut être supprimé ou altéré après validation.</p>
                  </div>
                </div>

                <div class="compliance-item">
                  <div class="comp-icon"><i class="fa-solid fa-file-circle-check"></i></div>
                  <div>
                    <h4>Mentions légales automatiques</h4>
                    <p>Pénalités de retard, indemnité forfaitaire de 40 €, attestation de capacité, assurance RC pro — toutes les mentions obligatoires sont incluses sur chaque document, sans effort.</p>
                  </div>
                </div>

                <div class="compliance-item highlight-comp">
                  <div class="comp-icon accent"><i class="fa-solid fa-network-wired"></i></div>
                  <div>
                    <h4>Prêt pour l'e-invoicing 2026 <span class="new-badge">Nouveau</span></h4>
                    <p>L'architecture de Termoflow est conçue pour se connecter à une Plateforme Agréée (PA) sans refonte. La réforme arrive, vous serez prêt le jour J.</p>
                  </div>
                </div>

              </div>
            </div>

            <div class="lg:w-1/2">
              <div class="seal-visual">
                <div class="seal-ring outer">
                  <div class="seal-ring mid">
                    <div class="seal-ring inner">
                      <div class="seal-core">
                        <i class="fa-solid fa-shield-halved"></i>
                        <span>RIAL</span>
                        <small>Certifié</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="seal-chips">
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> Anti-fraude TVA</div>
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> SHA-256</div>
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> Archivage 6 ans</div>
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> e-invoicing 2026</div>
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> Audit trail</div>
                  <div class="seal-chip"><i class="fa-solid fa-check"></i> Numéro séquentiel</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION PAIEMENTS ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row-reverse gap-16 items-center">
            <div class="lg:w-1/2">
              <div class="section-label">Suivi des paiements</div>
              <h2 class="section-title">Sachez exactement qui vous doit quoi,<br />en temps réel</h2>
              <p class="section-sub">Les relances sont chronophages et les impayés tuent la trésorerie. Termoflow automatise le suivi pour que vous ne perdiez plus d'argent par oubli.</p>

              <div class="payment-features mt-4">

                <div class="pay-feature">
                  <div class="pay-icon"><i class="fa-solid fa-coins"></i></div>
                  <div>
                    <h4>Enregistrement multi-modes</h4>
                    <p>Virement, chèque, espèces, carte bancaire — enregistrez chaque paiement reçu en quelques secondes, quel que soit le mode utilisé par votre client.</p>
                  </div>
                </div>

                <div class="pay-feature">
                  <div class="pay-icon"><i class="fa-solid fa-chart-pie"></i></div>
                  <div>
                    <h4>Paiements partiels &amp; acomptes</h4>
                    <p>Suivez en temps réel le montant réglé et le reste à payer sur chaque facture. Les acomptes versés sont automatiquement déduits du solde dû.</p>
                  </div>
                </div>

                <div class="pay-feature">
                  <div class="pay-icon"><i class="fa-solid fa-bell"></i></div>
                  <div>
                    <h4>Relances automatiques intelligentes</h4>
                    <p>Termoflow calcule la date de prochaine relance selon vos conditions de paiement et vous alerte sur les factures en retard. Fini les oublis.</p>
                  </div>
                </div>

                <div class="pay-feature">
                  <div class="pay-icon"><i class="fa-solid fa-stamp"></i></div>
                  <div>
                    <h4>Tampon PAYÉ automatique</h4>
                    <p>Dès qu'une facture est soldée, Termoflow la réémet avec un tampon visuel PAYÉ. Transmettez-la à votre client comme reçu sans aucune manipulation.</p>
                  </div>
                </div>

              </div>
            </div>

            <div class="lg:w-1/2">
              <div class="payment-dashboard">
                <div class="dash-title">Tableau de bord paiements</div>
                <div class="dash-kpis">
                  <div class="kpi green">
                    <span class="kpi-val">18 420 €</span>
                    <span class="kpi-lab">Encaissé ce mois</span>
                  </div>
                  <div class="kpi orange">
                    <span class="kpi-val">4 250 €</span>
                    <span class="kpi-lab">En attente</span>
                  </div>
                  <div class="kpi red">
                    <span class="kpi-val">1 180 €</span>
                    <span class="kpi-lab">En retard</span>
                  </div>
                </div>
                <div class="dash-invoices">
                  <div class="dash-inv-row">
                    <div class="inv-info">
                      <span class="inv-num">FAC-2025-041</span>
                      <span class="inv-client">Résidence Les Pins</span>
                    </div>
                    <div class="inv-right">
                      <span class="inv-amount">2 340 €</span>
                      <span class="inv-badge paid">Payé</span>
                    </div>
                  </div>
                  <div class="dash-inv-row">
                    <div class="inv-info">
                      <span class="inv-num">FAC-2025-042</span>
                      <span class="inv-client">M. Bernard</span>
                    </div>
                    <div class="inv-right">
                      <span class="inv-amount">890 €</span>
                      <span class="inv-badge partial">Partiel</span>
                    </div>
                  </div>
                  <div class="dash-inv-row">
                    <div class="inv-info">
                      <span class="inv-num">FAC-2025-038</span>
                      <span class="inv-client">Copropriété Voltaire</span>
                    </div>
                    <div class="inv-right">
                      <span class="inv-amount">1 180 €</span>
                      <span class="inv-badge late">Retard J+12</span>
                    </div>
                  </div>
                  <div class="dash-inv-row">
                    <div class="inv-info">
                      <span class="inv-num">FAC-2025-044</span>
                      <span class="inv-client">SCI Dupont &amp; Associés</span>
                    </div>
                    <div class="inv-right">
                      <span class="inv-amount">3 360 €</span>
                      <span class="inv-badge pending">En attente</span>
                    </div>
                  </div>
                </div>
                <div class="dash-relance">
                  <i class="fa-solid fa-bell" style="color:#f97316;"></i>
                  <span>Relance automatique envoyée à Copropriété Voltaire — il y a 2h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION CATALOGUE ===== -->
      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center max-w-3xl mx-auto mb-16">
            <div class="section-label">Catalogue de prestations</div>
            <h2 class="section-title">Votre bibliothèque de tarifs chauffagiste,<br />toujours à portée de main</h2>
            <p class="section-sub">Plus vous utilisez Termoflow, plus votre catalogue s'enrichit. Gagnez en vitesse à chaque devis.</p>
          </div>

          <div class="catalogue-showcase">
            <div class="catalogue-categories">
              <div class="cat-item active"><i class="fa-solid fa-fire-flame-curved"></i> Chauffage</div>
              <div class="cat-item"><i class="fa-solid fa-snowflake"></i> Climatisation</div>
              <div class="cat-item"><i class="fa-solid fa-wind"></i> VMC</div>
              <div class="cat-item"><i class="fa-solid fa-bolt"></i> PAC</div>
              <div class="cat-item"><i class="fa-solid fa-wrench"></i> Main d'œuvre</div>
              <div class="cat-item"><i class="fa-solid fa-boxes-stacked"></i> Fournitures</div>
            </div>
            <div class="catalogue-lines">
              <div class="cat-line">
                <div class="cat-line-name">
                  <strong>Chaudière gaz VIESSMAN 24kW</strong>
                  <small>Réf. VIE-24-COMPACT</small>
                </div>
                <div class="cat-line-margin">Marge : <strong class="green-text">32%</strong></div>
                <div class="cat-line-price">1 240 € HT</div>
                <button class="cat-add-btn">+ Ajouter</button>
              </div>
              <div class="cat-line">
                <div class="cat-line-name">
                  <strong>Installation chaudière murale (4h)</strong>
                  <small>Main d'œuvre</small>
                </div>
                <div class="cat-line-margin">Marge : <strong class="green-text">—</strong></div>
                <div class="cat-line-price">280 € HT</div>
                <button class="cat-add-btn">+ Ajouter</button>
              </div>
              <div class="cat-line active-line">
                <div class="cat-line-name">
                  <strong>PAC air/eau DAIKIN Altherma 8kW</strong>
                  <small>Réf. DAIK-AW8-R32</small>
                </div>
                <div class="cat-line-margin">Marge : <strong class="green-text">28%</strong></div>
                <div class="cat-line-price">3 800 € HT</div>
                <button class="cat-add-btn active-btn">✓ Ajouté</button>
              </div>
              <div class="cat-line">
                <div class="cat-line-name">
                  <strong>Thermostat connecté NEST (pose incluse)</strong>
                  <small>Réf. NEST-TH4</small>
                </div>
                <div class="cat-line-margin">Marge : <strong class="green-text">41%</strong></div>
                <div class="cat-line-price">195 € HT</div>
                <button class="cat-add-btn">+ Ajouter</button>
              </div>
            </div>
          </div>

          <div class="catalogue-tip">
            <i class="fa-solid fa-lightbulb"></i>
            <p><strong>Astuce :</strong> Si vous saisissez une nouvelle prestation directement sur un devis, sauvegardez-la en un clic dans votre catalogue pour la retrouver automatiquement sur vos prochains documents.</p>
          </div>
        </div>
      </section>

      <!-- ===== SECTION COORDONNÉES BANCAIRES ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-center">
            <div class="lg:w-1/2">
              <div class="section-label">Paramètres de paiement</div>
              <h2 class="section-title">Configurez une fois,<br />profitez sur tous vos documents</h2>
              <p class="section-sub mb-10">Vos coordonnées bancaires, conditions de paiement et taux de pénalités sont définis dans vos paramètres et appliqués automatiquement sur chaque facture. Zéro ressaisie, zéro oubli.</p>

              <div class="bank-features">
                <div class="bank-feat">
                  <div class="bank-icon"><i class="fa-solid fa-building-columns"></i></div>
                  <div>
                    <h4>IBAN appliqué automatiquement</h4>
                    <p>Renseignez votre IBAN une seule fois. Il apparaît sur toutes vos factures, dans le pied de page, prêt pour un virement.</p>
                  </div>
                </div>
                <div class="bank-feat">
                  <div class="bank-icon"><i class="fa-solid fa-calendar-days"></i></div>
                  <div>
                    <h4>Conditions de paiement flexibles</h4>
                    <p>30 jours net, à réception, comptant, 60 jours fin de mois — choisissez la règle par défaut ou ajustez-la client par client.</p>
                  </div>
                </div>
                <div class="bank-feat">
                  <div class="bank-icon"><i class="fa-solid fa-scale-balanced"></i></div>
                  <div>
                    <h4>Taux de pénalités personnalisable</h4>
                    <p>Définissez votre taux de pénalités de retard selon vos CGV. Termoflow l'inscrit automatiquement en bas de chaque facture, comme la loi l'exige.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:w-1/2">
              <div class="settings-card">
                <div class="settings-head">Paramètres de facturation</div>
                <div class="settings-body">
                  <div class="settings-field">
                    <label>IBAN</label>
                    <div class="settings-val iban">FR76 3000 4028 3700 0100 7500 943</div>
                  </div>
                  <div class="settings-field">
                    <label>Conditions de paiement</label>
                    <div class="settings-val">30 jours net date de facture</div>
                  </div>
                  <div class="settings-field">
                    <label>Pénalités de retard</label>
                    <div class="settings-val">3× taux d'intérêt légal</div>
                  </div>
                  <div class="settings-field">
                    <label>Indemnité forfaitaire</label>
                    <div class="settings-val">40 € (mention automatique)</div>
                  </div>
                  <div class="settings-field">
                    <label>Numérotation</label>
                    <div class="settings-val">DEV-AAAA-XXX / FAC-AAAA-XXX</div>
                  </div>
                  <div class="settings-field">
                    <label>RC Pro</label>
                    <div class="settings-val">AXA — police n° 4872-XXX-2025 ✓</div>
                  </div>
                </div>
                <div class="settings-footer">
                  <i class="fa-solid fa-circle-check" style="color:#22c55e;"></i>
                  Appliqué automatiquement sur tous les nouveaux documents
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION MOBILE ===== -->
      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row-reverse gap-16 items-center">
            <div class="lg:w-1/2">
              <div class="section-label">Sur le terrain</div>
              <h2 class="section-title">Créez et signez vos devis<br />directement chez le client</h2>
              <p class="section-sub mb-10">Pas besoin de rentrer au bureau pour envoyer un devis. Termoflow est parfaitement adapté aux smartphones et tablettes — y compris en zone blanche.</p>

              <div class="mobile-feats">
                <div class="mob-feat">
                  <i class="fa-solid fa-mobile-screen-button"></i>
                  <span>Interface optimisée mobile &amp; tablette</span>
                </div>
                <div class="mob-feat">
                  <i class="fa-solid fa-eye"></i>
                  <span>Aperçu PDF en temps réel avant envoi</span>
                </div>
                <div class="mob-feat">
                  <i class="fa-solid fa-envelope"></i>
                  <span>Envoi par email intégré, corps pré-rempli</span>
                </div>
                <div class="mob-feat">
                  <i class="fa-solid fa-signature"></i>
                  <span>Signature électronique du client sur place</span>
                </div>
                <div class="mob-feat">
                  <i class="fa-solid fa-wifi"></i>
                  <span>Fonctionne sans connexion (sync différée)</span>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2">
              <div class="mobile-mockup-wrap">
                <div class="mobile-phone">
                  <div class="phone-notch"></div>
                  <div class="phone-screen">
                    <div class="phone-header">
                      <span>Nouveau devis</span>
                      <span class="phone-save">Sauvegarder</span>
                    </div>
                    <div class="phone-client-picker">
                      <i class="fa-solid fa-user-circle"></i>
                      <div>
                        <strong>M. Fontaine, Jean</strong>
                        <small>45 rue de la Paix — Grenoble</small>
                      </div>
                    </div>
                    <div class="phone-lines">
                      <div class="phone-line-item">
                        <span>PAC DAIKIN 8kW</span>
                        <span class="phone-price">3 800 €</span>
                      </div>
                      <div class="phone-line-item">
                        <span>MO installation (8h)</span>
                        <span class="phone-price">520 €</span>
                      </div>
                      <div class="phone-add-line">+ Ajouter depuis catalogue</div>
                    </div>
                    <div class="phone-total">
                      <span>Total TTC</span>
                      <span class="phone-total-val">4 696,20 €</span>
                    </div>
                    <button class="phone-cta">Envoyer le devis</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== SECTION INTÉGRATION ===== -->
      <section class="py-24 bg-white">
        <div class="max-w-5xl mx-auto px-4 text-center">
          <div class="section-label">Compatibilité</div>
          <h2 class="section-title">Vous avez déjà un logiciel de facturation ?</h2>
          <p class="section-sub mb-12">Termoflow s'adapte à votre organisation. Si vous avez déjà un outil en place, connectez-le directement — toute la gestion de vos chantiers reste dans Termoflow, vos factures sont générées dans votre logiciel habituel, sans double saisie.</p>

          <div class="integration-choice">
            <div class="integ-card featured-integ">
              <div class="integ-badge">Recommandé</div>
              <div class="integ-icon native"><i class="fa-solid fa-fire-burner"></i></div>
              <h3>Moteur natif Termoflow</h3>
              <p>Inclus sans supplément. Activez-le en un clic depuis vos paramètres. Aucun tiers, aucune synchronisation à gérer.</p>
              <div class="integ-avantages">
                <span><i class="fa-solid fa-check"></i> Conformité anti-fraude</span>
                <span><i class="fa-solid fa-check"></i> e-invoicing 2026</span>
                <span><i class="fa-solid fa-check"></i> 0 € supplémentaire</span>
              </div>
            </div>
            <div class="integ-or">ou</div>
            <div class="integ-card">
              <div class="integ-icon external"><i class="fa-solid fa-plug"></i></div>
              <h3>Votre outil existant</h3>
              <p>Connectez VosFactures ou tout outil disposant d'une API REST. Vos données de chantier arrivent automatiquement dans votre logiciel de facturation.</p>
              <div class="integ-logos">
                <span class="integ-logo">VosFactures</span>
                <span class="integ-logo">API REST</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CHIFFRES ===== -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4">
          <h2 class="text-center text-3xl font-bold text-white mb-16">Termoflow, c'est du concret pour votre activité</h2>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">3 min</div>
              <p class="benefit-lab-dark">Pour créer un devis complet depuis le catalogue</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">1 clic</div>
              <p class="benefit-lab-dark">Pour convertir un devis en facture + acompte + solde</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">0 €</div>
              <p class="benefit-lab-dark">De surcoût — inclus dans votre abonnement entreprise Termoflow</p>
            </div>
            <div class="benefit-box-dark">
              <div class="benefit-val-dark">6 ans</div>
              <p class="benefit-lab-dark">D'archivage sécurisé, conforme aux obligations fiscales</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CTA FINAL ===== -->
      <section class="py-24 text-center bg-white">
        <div class="max-w-3xl mx-auto px-4">
          <h2 class="text-4xl font-black text-gray-900 mb-6">
            Un logiciel de facturation pour chauffagiste,<br />
            <span class="gradient-text-orange">conforme, complet et inclus.</span>
          </h2>
          <p class="text-xl text-gray-500 mb-10">Concentrez-vous sur vos chantiers. On s'occupe du reste.</p>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
            <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'FacturationPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* ==================== UTILS ==================== */
.gradient-text-orange {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.green-text { color: #16a34a; }

/* ==================== HERO ==================== */
.hero-facturation {
  padding: 10rem 0 6rem;
  background: radial-gradient(ellipse at top right, #fff7ed, transparent 50%),
              radial-gradient(ellipse at bottom left, #fef2f2, transparent 50%);
}

.badge-facturation {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #fff7ed;
  color: #c2410c;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  border: 1px solid #fed7aa;
  letter-spacing: 0.05em;
}

/* Hero stats */
.hero-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid #f1f5f9;
}
.stat-pill {
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  gap: 0.1rem;
}
.stat-val { font-size: 1.1rem; font-weight: 900; color: #f97316; }
.stat-lab { font-size: 0.7rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }

/* ==================== INVOICE UI MOCKUP ==================== */
.invoice-ui {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.ui-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ui-tabs { display: flex; gap: 0.5rem; }
.ui-tab {
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  cursor: default;
}
.ui-tab.active { background: white; color: #1e293b; border: 1px solid #e2e8f0; }
.ui-status {
  font-size: 0.7rem;
  font-weight: 800;
  padding: 0.35rem 0.8rem;
  border-radius: 9999px;
}
.ui-status.validated { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }

.ui-body { padding: 1.5rem; }
.invoice-client-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
}
.inv-label { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; color: #94a3b8; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
.inv-val { font-size: 0.85rem; font-weight: 700; color: #1e293b; }

.invoice-lines { border: 1px solid #e5e7eb; border-radius: 0.75rem; overflow: hidden; margin-bottom: 1.25rem; }
.inv-line-head {
  display: grid;
  grid-template-columns: 2fr 0.5fr 1fr 0.5fr 1fr;
  gap: 0.5rem;
  background: #f8fafc;
  padding: 0.6rem 1rem;
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 0.05em;
}
.inv-line {
  display: grid;
  grid-template-columns: 2fr 0.5fr 1fr 0.5fr 1fr;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  font-size: 0.78rem;
  color: #374151;
  border-top: 1px solid #f1f5f9;
  font-weight: 600;
}
.highlight-line { background: #fff7ed; }

.inv-totals {
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
}
.tot-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #6b7280;
  padding: 0.25rem 0;
  font-weight: 600;
}
.tot-row.bold { color: #1e293b; font-weight: 900; font-size: 0.95rem; border-top: 1px solid #e5e7eb; margin-top: 0.5rem; padding-top: 0.5rem; }
.tot-row.acompte { color: #16a34a; }
.tot-row.solde { color: #f97316; font-weight: 900; font-size: 0.9rem; }

.inv-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.inv-btn-primary {
  background: linear-gradient(to right, #f97316, #ef4444);
  color: white;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.6rem 1.25rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
}
.inv-btn-sec {
  background: white;
  color: #374151;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.6rem 1.25rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
}

.ui-footer-seal {
  background: #1e293b;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.65rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: 0.03em;
}

/* ==================== TRUST BAR ==================== */
.trust-bar {
  background: #1e293b;
  padding: 1.25rem 0;
}
.trust-items {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 2rem;
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 700;
}
.trust-item i { color: #f97316; font-size: 0.9rem; }
.trust-sep { width: 1px; height: 1.25rem; background: #334155; }

/* ==================== SECTIONS COMMUNES ==================== */
.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #f97316;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 2.25rem;
  font-weight: 900;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 1rem;
}
.section-sub {
  font-size: 1.05rem;
  color: #6b7280;
  line-height: 1.7;
  max-width: 38rem;
}

/* ==================== FEATURES GRID ==================== */
.features-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
}
@media (max-width: 900px) { .features-grid-3 { grid-template-columns: 1fr 1fr; } }
@media (max-width: 600px) { .features-grid-3 { grid-template-columns: 1fr; } }

.feat-card {
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 1.25rem;
  padding: 2rem;
  transition: box-shadow 0.2s, transform 0.2s;
}
.feat-card:hover { box-shadow: 0 8px 30px rgba(0,0,0,0.07); transform: translateY(-2px); }

.feat-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  margin-bottom: 1.25rem;
}
.feat-icon.orange { background: #fff7ed; color: #f97316; }
.feat-card h3 { font-size: 1rem; font-weight: 800; color: #111827; margin-bottom: 0.75rem; }
.feat-card p { font-size: 0.9rem; color: #6b7280; line-height: 1.65; }

/* ==================== SECTION DARK / CONFORMITÉ ==================== */
.section-dark { background: #0f172a; }
.section-label-light {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #f97316;
  margin-bottom: 1rem;
}
.compliance-list { display: flex; flex-direction: column; gap: 1.5rem; }
.compliance-item {
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid #1e293b;
}
.compliance-item.highlight-comp { border-color: #f97316; background: rgba(249,115,22,0.05); }
.comp-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: #1e293b;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.comp-icon.accent { background: rgba(249,115,22,0.15); color: #f97316; }
.compliance-item h4 { font-size: 0.9rem; font-weight: 800; color: #f1f5f9; margin-bottom: 0.4rem; display: flex; align-items: center; gap: 0.6rem; }
.compliance-item p { font-size: 0.82rem; color: #64748b; line-height: 1.6; }
.new-badge {
  background: #f97316;
  color: white;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

/* Seal visual */
.seal-visual { display: flex; flex-direction: column; align-items: center; gap: 2.5rem; }
.seal-ring {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.seal-ring.outer { width: 300px; height: 300px; border: 2px solid #1e293b; background: rgba(15,23,42,0.5); }
.seal-ring.mid  { width: 230px; height: 230px; border: 2px solid #334155; background: rgba(30,41,59,0.6); }
.seal-ring.inner{ width: 160px; height: 160px; border: 2px solid #f97316; background: #111827; }
.seal-core { text-align: center; color: white; }
.seal-core i { font-size: 2.5rem; color: #f97316; display: block; margin-bottom: 0.5rem; }
.seal-core span { font-size: 1.2rem; font-weight: 900; display: block; }
.seal-core small { font-size: 0.65rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; }
.seal-chips { display: flex; flex-wrap: wrap; gap: 0.6rem; justify-content: center; }
.seal-chip {
  background: #1e293b;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 1rem;
  border-radius: 9999px;
  border: 1px solid #334155;
}
.seal-chip i { color: #22c55e; margin-right: 0.4rem; }

/* ==================== PAYMENT DASHBOARD ==================== */
.payment-dashboard {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.dash-title {
  background: #f8fafc;
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  border-bottom: 1px solid #e5e7eb;
}
.dash-kpis { display: grid; grid-template-columns: repeat(3,1fr); gap: 0; border-bottom: 1px solid #e5e7eb; }
.kpi { padding: 1.25rem; text-align: center; }
.kpi + .kpi { border-left: 1px solid #e5e7eb; }
.kpi-val { display: block; font-size: 1.3rem; font-weight: 900; margin-bottom: 0.25rem; }
.kpi-lab { font-size: 0.7rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }
.kpi.green .kpi-val { color: #16a34a; }
.kpi.orange .kpi-val { color: #f97316; }
.kpi.red .kpi-val { color: #ef4444; }

.dash-invoices { padding: 0.5rem 0; }
.dash-inv-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
}
.inv-info { display: flex; flex-direction: column; gap: 0.15rem; }
.inv-num { font-size: 0.75rem; font-weight: 800; color: #1e293b; }
.inv-client { font-size: 0.7rem; color: #94a3b8; font-weight: 600; }
.inv-right { display: flex; align-items: center; gap: 0.75rem; }
.inv-amount { font-size: 0.85rem; font-weight: 800; color: #1e293b; }
.inv-badge {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.25rem 0.7rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.inv-badge.paid { background: #f0fdf4; color: #166534; }
.inv-badge.partial { background: #fff7ed; color: #c2410c; }
.inv-badge.late { background: #fef2f2; color: #991b1b; }
.inv-badge.pending { background: #f0f9ff; color: #0c4a6e; }

.dash-relance {
  padding: 0.85rem 1.5rem;
  background: #fff7ed;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #92400e;
}

/* Payment features */
.payment-features { display: flex; flex-direction: column; gap: 1.5rem; }
.pay-feature { display: flex; gap: 1.25rem; }
.pay-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: #fff7ed;
  color: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}
.pay-feature h4 { font-size: 0.95rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
.pay-feature p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; }

/* ==================== CATALOGUE ==================== */
.catalogue-showcase {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 1.5rem;
  overflow: hidden;
}
.catalogue-categories {
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  padding: 1rem 0;
}
.cat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.5rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #94a3b8;
  cursor: default;
}
.cat-item i { font-size: 0.9rem; }
.cat-item.active { background: white; color: #f97316; border-right: 2px solid #f97316; }

.catalogue-lines { padding: 0.5rem 0; }
.cat-line {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
}
.cat-line.active-line { background: #fff7ed; }
.cat-line-name strong { display: block; font-size: 0.85rem; font-weight: 800; color: #1e293b; }
.cat-line-name small { font-size: 0.7rem; color: #94a3b8; font-weight: 600; }
.cat-line-margin { font-size: 0.8rem; color: #6b7280; font-weight: 700; white-space: nowrap; }
.cat-line-price { font-size: 0.9rem; font-weight: 900; color: #1e293b; white-space: nowrap; }
.cat-add-btn {
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
}
.cat-add-btn.active-btn { background: #f97316; color: white; border-color: #f97316; }

.catalogue-tip {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-top: 1.5rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
}
.catalogue-tip i { color: #0284c7; font-size: 1.1rem; flex-shrink: 0; margin-top: 0.1rem; }
.catalogue-tip p { font-size: 0.87rem; color: #0c4a6e; line-height: 1.6; margin: 0; }

/* ==================== SETTINGS CARD ==================== */
.settings-card {
  background: white;
  border-radius: 1.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}
.settings-head {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}
.settings-body { padding: 0.5rem 0; }
.settings-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
  gap: 1rem;
}
.settings-field label { font-size: 0.8rem; font-weight: 700; color: #94a3b8; flex-shrink: 0; }
.settings-val { font-size: 0.82rem; font-weight: 700; color: #1e293b; text-align: right; }
.settings-val.iban { font-family: monospace; font-size: 0.78rem; color: #374151; }
.settings-footer {
  background: #f0fdf4;
  border-top: 1px solid #dcfce7;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #166534;
}

/* ==================== BANK FEATURES ==================== */
.bank-features { display: flex; flex-direction: column; gap: 1.5rem; }
.bank-feat { display: flex; gap: 1.25rem; }
.bank-icon {
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: #fff7ed;
  color: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}
.bank-feat h4 { font-size: 0.95rem; font-weight: 800; color: #111827; margin-bottom: 0.4rem; }
.bank-feat p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; }

/* ==================== MOBILE MOCKUP ==================== */
.mobile-mockup-wrap { display: flex; justify-content: center; }
.mobile-phone {
  width: 280px;
  background: #1e293b;
  border-radius: 3rem;
  padding: 1rem;
  box-shadow: 0 30px 80px rgba(0,0,0,0.2);
  position: relative;
}
.phone-notch {
  width: 80px;
  height: 10px;
  background: #0f172a;
  border-radius: 9999px;
  margin: 0 auto 1rem;
}
.phone-screen {
  background: white;
  border-radius: 2rem;
  padding: 1.25rem;
  min-height: 420px;
}
.phone-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  font-size: 0.85rem;
  font-weight: 800;
  color: #1e293b;
}
.phone-save { font-size: 0.7rem; color: #f97316; font-weight: 800; }
.phone-client-picker {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 0.85rem;
  margin-bottom: 1.25rem;
}
.phone-client-picker i { font-size: 1.5rem; color: #94a3b8; }
.phone-client-picker strong { display: block; font-size: 0.8rem; font-weight: 800; color: #1e293b; }
.phone-client-picker small { font-size: 0.68rem; color: #94a3b8; font-weight: 600; }
.phone-lines { margin-bottom: 1rem; }
.phone-line-item {
  display: flex;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.78rem;
  font-weight: 700;
  color: #374151;
}
.phone-price { color: #1e293b; }
.phone-add-line {
  font-size: 0.72rem;
  color: #f97316;
  font-weight: 800;
  padding: 0.6rem 0;
  cursor: default;
}
.phone-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 0.85rem;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #6b7280;
}
.phone-total-val { font-size: 1rem; font-weight: 900; color: #f97316; }
.phone-cta {
  width: 100%;
  background: linear-gradient(to right, #f97316, #ef4444);
  color: white;
  border: none;
  padding: 0.85rem;
  border-radius: 9999px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
}

/* Mobile features list */
.mobile-feats { display: flex; flex-direction: column; gap: 1rem; margin-top: 2rem; }
.mob-feat {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #374151;
}
.mob-feat i { width: 2rem; text-align: center; color: #f97316; font-size: 1rem; }

/* ==================== INTÉGRATION ==================== */
.integration-choice {
  display: flex;
  align-items: center;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
}
.integ-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 1.5rem;
  padding: 2.5rem;
  width: 320px;
  position: relative;
}
.integ-card.featured-integ { border: 2px solid #f97316; }
.integ-badge {
  position: absolute;
  top: -1rem;
  left: 50%;
  transform: translateX(-50%);
  background: #f97316;
  color: white;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 0.3rem 1rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}
.integ-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-bottom: 1.25rem;
}
.integ-icon.native { background: #fff7ed; color: #f97316; }
.integ-icon.external { background: #f0f9ff; color: #0284c7; }
.integ-card h3 { font-size: 1.1rem; font-weight: 900; color: #111827; margin-bottom: 0.75rem; }
.integ-card p { font-size: 0.87rem; color: #6b7280; line-height: 1.6; margin-bottom: 1.25rem; }
.integ-avantages { display: flex; flex-direction: column; gap: 0.4rem; }
.integ-avantages span { font-size: 0.8rem; font-weight: 700; color: #374151; }
.integ-avantages i { color: #22c55e; margin-right: 0.5rem; }
.integ-or { font-size: 1rem; font-weight: 800; color: #94a3b8; }
.integ-logos { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.integ-logo {
  background: #f8fafc;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.4rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

/* ==================== BENEFIT BOXES ==================== */
.benefit-box-dark {
  background: #1e293b;
  padding: 2.5rem 2rem;
  border-radius: 1.5rem;
  text-align: center;
  border: 1px solid #334155;
}
.benefit-val-dark { font-size: 2.75rem; font-weight: 900; color: #f97316; margin-bottom: 0.75rem; }
.benefit-lab-dark { font-size: 0.87rem; font-weight: 700; color: #64748b; line-height: 1.5; }

/* ==================== BUTTONS ==================== */
.btn-primary { background: linear-gradient(to right, #f97316, #ef4444); color: white; padding: 1rem 2.5rem; border-radius: 9999px; font-weight: 800; border: none; cursor: pointer; font-size: 0.95rem; }
.btn-primary-large { background: #f97316; color: white; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; border: none; font-size: 1.1rem; cursor: pointer; }
.btn-outline-large { border: 2px solid #e5e7eb; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; color: #1f2937; text-decoration: none; display: inline-block; font-size: 1rem; }

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1024px) {
  .hero-facturation { padding-top: 8rem; text-align: center; }
  .invoice-ui { margin-top: 3rem; }
  .section-title { font-size: 1.75rem; }
  .catalogue-showcase { grid-template-columns: 1fr; }
  .catalogue-categories { display: flex; overflow-x: auto; border-right: none; border-bottom: 1px solid #e5e7eb; padding: 0.5rem; gap: 0.25rem; }
  .cat-item.active { border-right: none; border-bottom: 2px solid #f97316; }
  .integ-card { width: 280px; }
}
</style>